import * as React from "react";
import {useContext, useEffect, useMemo, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {AgGridReact} from "ag-grid-react";
import {ColDef, GetRowIdParams, GridApi, GridOptions, IServerSideGetRowsParams} from "ag-grid-enterprise";
import IconButton from "@mui/material/IconButton";
import {useRecoilState, useRecoilValue, useResetRecoilState, useSetRecoilState} from "recoil";
import {
    STATE_DISABLE_TAB,
    STATE_REPORT_ACTIVE_FILTERS,
    STATE_REPORT_TOTAL_COUNT,
    STATE_REPORT_UPDATING
} from "../constants/EnelxRecoil";
import {STATE_SUMMARY_UPDATING} from "../../../constants/FinanceRecoil";
import {FilterDef} from "../../../constants/FinanceModels";
import ChipDynamic from "../components/ChipDynamic";
import {ID_IMPORTED, ID_LOADING, ID_PROCESSED} from "../constants/EnelxStatuses";
import {GlobalContext} from "../../../../../util/state/GlobalContextProvider";
import {setSnackbar} from "../../../../../util/state/globalAction";
import {CloudUploadRounded, DeleteForeverRounded, DescriptionRounded, InfoRounded} from "@mui/icons-material";
import {Button, Stack, Tooltip, useTheme} from "@mui/material";
import {useReportRepository} from "../../../api/repositories/ReportRepository";
import {snackbarTypes} from "../../../../../util/state/globalVariables";
import {formatDate} from "../../../utils/FormatUtils";
import {getLocalGridState, setLocalGridState} from "../../../utils/GridUtils";
import FinanceDialog from "../../../component/FinanceDialog";
import FinanceDynamicActions from "../../../component/FinanceDynamicActions";
import DropFileTemplate from "../components/DropFileTemplate";
import AgGridCustomTable from "../../../../../util/component/AgGridCustomTable";
import {NumericSummary} from "../../../component/NumericSummary";
import {useAgGridLocale, useHasScope} from "../../../../../config/globalConfig";
import {ENELX_DELETE_REPORT, ENELX_IMPORT_REPORTS, ENELX_VIEW_REPORT_DETAIL} from "../../../utils/FinanceScope";
import ReportModal from "../modals/ReportModal";

const ReportTable = () => {

    const UNIQUE_ID = "fnn-report";

    const { t } = useTranslation('finance', { keyPrefix: 'enelx' });
    const { getReports, deleteReport } = useReportRepository();

    const { dispatchGlobalData, globalData: { user } } = useContext(GlobalContext);
    const hasScope = useHasScope;
    const setTotalCount = useSetRecoilState(STATE_REPORT_TOTAL_COUNT);

    const [updating, setUpdating] = useRecoilState(STATE_REPORT_UPDATING);
    const setSummaryUpdating = useSetRecoilState(STATE_SUMMARY_UPDATING);
    const { getSummary } = useReportRepository();

    const reportFilterDefs = useRecoilValue(STATE_REPORT_ACTIVE_FILTERS);

    const [openDeleteWarning, setOpenDeleteWarning] = useState(false);
    const [deleteWarningReportId, setDeleteWarningReportId] = useState("");
    const [deleteWarningReportName, setDeleteWarningReportName] = useState("");
    const [disableTab, setDisableTab] = useRecoilState(STATE_DISABLE_TAB);
    const resetDisableTab = useResetRecoilState(STATE_DISABLE_TAB);
    const theme = useTheme();
    const gridRef = useRef<AgGridReact>(null);

    const legalEntities: any[] = user.legalEntities?.filter((x: any) => x.name.toLowerCase() !== "all");
    const dealers: any[] = user.dealers?.filter((x: any) => x.name.toLowerCase() !== "all");

    const agGridLocale = useAgGridLocale();
    const [openReportModal, setOpenReportModal] = useState(false);
    const redrawRows = () => {
        setSummaryUpdating(true);
        gridRef?.current?.api.redrawRows();
        dispatchGlobalData({
            type: setSnackbar,
            payload: {
                snackbar: {
                    message: t("snackbars.imported"),
                    type: snackbarTypes.success,
                    show: true,
                    autoClose: true,
                }
            }
        });
    }


    const columnDefs: ColDef[] = [
        {
            field: 'name',
            headerName: t('tables.report.report-name'),
            headerTooltip: t('tables.report.report-name'),
            suppressSizeToFit: true,
            minWidth: 250,
            unSortIcon: true,
            pinned: "left",
            lockPosition: "left",
            lockVisible: true,
            lockPinned: true
        },
        {
            field: 'createdBy',
            headerName: t('tables.report.imported-by'),
            headerTooltip: t('tables.report.imported-by'),
            minWidth: 200,
            unSortIcon: true
        },
        {
            field: 'created',
            headerName: t('tables.report.importing-date'),
            headerTooltip: t('tables.report.importing-date'),
            minWidth: 200,
            unSortIcon: true,
            valueFormatter: params => formatDate(params.value)
        },
        {
            field: 'rowCount',
            headerName: t('tables.report.recharges-count'),
            headerTooltip: t('tables.report.recharges-count'),
            minWidth: 200,
            unSortIcon: true
        },
        {
            field: 'status',
            headerName: t('tables.report.status'),
            headerTooltip: t('tables.report.status'),
            minWidth: 150, suppressSizeToFit: true,
            sortable: true, unSortIcon: true,
            lockPosition: "right", lockVisible: true, lockPinned: true, pinned: "right",
            cellRendererSelector: (params) => {
                return {
                    component: () => <ChipDynamic id={params.data.id} interval={10000} data={params.data}
                                                  redrawRows={redrawRows}
                                                  initialValue={params.data.status}/>
                };
            },
        },
        {
            field: "legalEntity", headerName: t("tables.report.legalEntity"),
            minWidth: 180,
            unSortIcon: true, sortable: true,  hide: legalEntities?.length <= 1
        },
        {
            field: "dealer", headerName: t("tables.report.dealer"),
            minWidth: 180,
            unSortIcon: true, sortable: true, hide: dealers?.length <= 1
        },
        {
            field: '_actions', headerName: '',
            suppressSizeToFit: true, minWidth: 100, maxWidth: 110, sortable: false,
            pinned: 'right', lockPosition: 'right', lockVisible: true, lockPinned: true,
            cellRendererSelector: (params) => {
                return {
                    component: () =>
                            <FinanceDynamicActions>
                                {hasScope(ENELX_VIEW_REPORT_DETAIL) &&
                                        <IconButton disabled={params.data.status === ID_LOADING} sx={{ color: theme.palette.accent.main }} onClick={() => {
                                            setDisableTab(true);
                                            window.open('/finance/enel-x/report/' + params.data.id, "_blank")
                                        }}>
                                            <Tooltip arrow title={t('tables.report.actions.tooltips.view')}>
                                                <DescriptionRounded/>
                                            </Tooltip>
                                        </IconButton>
                                }
                                {hasScope(ENELX_DELETE_REPORT) &&
                                        <IconButton disabled={params.data.status !== ID_IMPORTED} sx={{ color: theme.palette.accent.main }} onClick={() => {
                                            setDeleteWarningReportId(params.data.id);
                                            setDeleteWarningReportName(params.data.name);
                                            setOpenDeleteWarning(true);
                                        }}>
                                            <Tooltip arrow title={t('tables.report.actions.tooltips.delete')}>
                                                <DeleteForeverRounded/>
                                            </Tooltip>
                                        </IconButton>
                                }
                            </FinanceDynamicActions>
                };
            },
            suppressFiltersToolPanel: true
        }
    ];

    const gridOptions: GridOptions = {
        getRowId: (params: GetRowIdParams) => params.data['id'],
        defaultColDef: {
            sortable: true,
            resizable: true,
            wrapHeaderText: false,
        },
    };

    useEffect(() => {
        if (reportFilterDefs.length === 0) {
            setTotalCount(null);
        }
        gridRef.current?.api?.setServerSideDatasource(createDatasource(reportFilterDefs, gridRef.current.api));
        setSummaryUpdating(true);
    }, [reportFilterDefs])

    useEffect(() => {
        if (updating) {
            gridRef.current?.api?.setServerSideDatasource(createDatasource(reportFilterDefs, gridRef.current.api));
            setUpdating(false);
            setSummaryUpdating(true);
        }
    }, [updating])

    useEffect(() => {
        setSummaryUpdating(true);
    }, [agGridLocale]);

    const createDatasource = (filterDefs: FilterDef[], api: GridApi<any>) => {
        return {
            getRows: async (params: IServerSideGetRowsParams) => {
                let startRow = params.request.startRow ? params.request.startRow : 0;
                let endRow = params.request.endRow ? params.request.endRow : 10;
                let sort = params.request.sortModel.length === 0 ? null : params.request.sortModel[0].colId;
                let order = params.request.sortModel.length === 0 ? null : params.request.sortModel[0].sort;

                getReports(startRow, endRow - startRow, sort, order, filterDefs)
                        .then(response => {
                            let result = response.result;

                            setTotalCount(result.page.totalElements);

                            if (result.page.totalElements === 0) {
                                api.showNoRowsOverlay();
                            } else {
                                api.hideOverlay();
                            }

                            params.success({
                                rowData: result.content,
                                rowCount: result.page.totalElements
                            })
                        })
                        .catch(() => params.fail());
            }
        };
    }

    const noRowsOverlayComponent = useMemo<any>(() => {
        return DropFileTemplate;
    }, []);

    function saveState() {
        setLocalGridState(UNIQUE_ID, {
            rows: gridRef.current!.api.paginationGetPageSize(),
            cols: gridRef.current!.columnApi.getColumnState()
        });
    }

    const toolbar = {
        renderInfo: () => (
                <NumericSummary query={() => getSummary(reportFilterDefs)} fields={[ID_LOADING, ID_IMPORTED, ID_PROCESSED]} i18nPath={"enelx.counters.reports"}/>
        ),
        renderActions: () => (

                <Stack direction={'row'} spacing={3}>
                    {hasScope(ENELX_IMPORT_REPORTS) &&
                            <Button color={"accent"} variant="contained" component="label" startIcon={<CloudUploadRounded/>}
                                onClick={() => setOpenReportModal(true)}>
                                {t('import.button')}
                            </Button>}
                </Stack>

        ),
    };

    return (
            <>
                <AgGridCustomTable
                        toolbar={toolbar}
                        rowModelType='serverSide'
                        paginationPageSize={getLocalGridState(UNIQUE_ID).rows} serverSideInfiniteScroll
                        gridRef={gridRef} columnDefs={columnDefs} gridOptions={gridOptions}
                        rowSelection={'multiple'}
                        noRowsOverlayComponent={noRowsOverlayComponent}
                        suppressRowClickSelection
                        onGridReady={() => {
                            gridRef.current!.api.setServerSideDatasource(createDatasource(reportFilterDefs, gridRef.current!.api));

                            let gridState = getLocalGridState(UNIQUE_ID).cols;
                            if (gridState !== undefined) {
                                gridRef.current!.columnApi.applyColumnState({
                                    state: gridState,
                                    applyOrder: true
                                })
                            }
                        }}
                        onColumnMoved={e => {
                            if (e.source === "api") return;
                            saveState();
                        }}
                        onColumnPinned={e => {
                            if (e.source === "api") return;
                            saveState();
                        }}
                        onColumnResized={e => {
                            if (e.source === "api") return;
                            saveState();
                        }}
                        onSortChanged={e => {
                            if (e.source === "api") return;
                            saveState();
                        }}
                        onPaginationChanged={() => {
                            setLocalGridState(UNIQUE_ID, {
                                rows: gridRef.current!.api?.paginationGetPageSize(),
                                cols: getLocalGridState(UNIQUE_ID).cols
                            });
                        }}
                        onGridSizeChanged={() => gridRef.current!.api.sizeColumnsToFit()}
                />
                <ReportModal open={openReportModal}
                       onClose={() => {
                           setOpenReportModal(false)
                       }}
                />
                <FinanceDialog open={openDeleteWarning}
                               onClose={() => setOpenDeleteWarning(false)}
                               callback={() => deleteReport(deleteWarningReportId).then(() => setUpdating(true))}
                               titleIcon={<InfoRounded sx={{ color: 'accent.main' }}/>}
                               title={t('delete-confirmation.title')}
                               content={t('delete-confirmation.text', { reportName: deleteWarningReportName })}
                               confirmButton={t('delete-confirmation.confirm')}
                               cancelButton={t('delete-confirmation.cancel')}
                />
            </>

    );
}

export default ReportTable;